import React, {
  useState,
  useEffect,
} from "react"

import {
  useParams,
} from "react-router"

import useTitle
  from "@/hooks/useTitle"

import PulseLoader
  from "react-spinners/PulseLoader"

import ModalSubmissionConfirm
  from "@/components/common/modal/ModalSubmissionConfirm"

import {
  useVerifyOrgIntentionPublicAccessMutation,
  useLazyGetPublicOrgIntentionResponseQuery,
  useSavePublicOrgIntentionResponseMutation,
  useSubmitPublicOrgIntentionResponseMutation,
} from "@/features/orgIntentionBoards/orgIntentionResponsesApiSlice"

// 08 Sep 2026
import {
  PlusIcon,
  MinusIcon,
} from "@heroicons/react/24/outline"

const EtPublicOrgIntentionBoard = () => {

  // First Created - 04 Sep 2026
  // Author - Atul Pandey
  // Purpose - Public access page for Trinity Intention Board candidates.
  // Updated - 05 Sep 2026 - Added public board, candidate,
  //                           question and response loading.
  // Updated - 05 Sep 2026 - Added editable responses and Save Draft.
  // Updated - 05 Sep 2026 - Refined editable / read only state messaging.
  // Updated - 05 Sep 2026 - Added user timezone, localized deadline,
  //                           and current user date / time.
  // Updated - 05 Sep 2026 - Added deadline countdown timeline and
  //                           adaptive public board refresh.
  // Updated - 05 Sep 2026 - Added final Submit Response flow.
  // Updated - 05 Sep 2026 - Protected unsaved responses during
  //                           background deadline refresh.
  // Updated - 05 Sep 2026 - Added submitted-before-deadline,
  //                           automatic deadline submission and
  //                           overdue incomplete response states.
  // Updated - 05 Sep 2026 - Corrected React Hook ordering.
  // Updated - 06 Sep 2026 - Backend-owned automatic deadline submission.
  // Updated - 06 Sep 2026 - Added response-window elapsed timeline using
  //                           responseOpenedAt -> now -> responseDeadline.
  // Updated - 08 Sep 2026 - Rich Text refinements

  useTitle(
    "Trinity Intention Board"
  )

  /*
  |--------------------------------------------------------------------------
  | 1 - Public Slug
  |--------------------------------------------------------------------------
  */

  const {
    publicSlug,
  } =
    useParams()

  /*
  |--------------------------------------------------------------------------
  | 2 - User Timezone
  |--------------------------------------------------------------------------
  */

  const userTimezone =
    Intl
      .DateTimeFormat()
      .resolvedOptions()
      .timeZone ||
    "UTC"

  /*
  |--------------------------------------------------------------------------
  | 3 - Current Date / Time
  |--------------------------------------------------------------------------
  */

  const [
    currentDateTime,
    setCurrentDateTime,
  ] =
    useState(
      new Date()
    )

  /*
  |--------------------------------------------------------------------------
  | 4 - Verification Mutation
  |--------------------------------------------------------------------------
  */

  const [
    verifyOrgIntentionPublicAccess,
    {
      isLoading:
        isVerifying,

      isError:
        isVerificationError,

      error:
        verificationError,
    },
  ] =
    useVerifyOrgIntentionPublicAccessMutation()

  /*
  |--------------------------------------------------------------------------
  | 5 - Public Response Query
  |--------------------------------------------------------------------------
  */

  const [
    getPublicOrgIntentionResponse,
    {
      data:
        publicIntentionData,

      isFetching:
        isFetchingPublicIntention,

      isError:
        isPublicIntentionError,

      error:
        publicIntentionError,
    },
  ] =
    useLazyGetPublicOrgIntentionResponseQuery()

  /*
  |--------------------------------------------------------------------------
  | 6 - Save Draft Mutation
  |--------------------------------------------------------------------------
  */

  const [
    savePublicOrgIntentionResponse,
    {
      isLoading:
        isSavingDraft,

      isSuccess:
        isSaveDraftSuccess,

      isError:
        isSaveDraftError,

      error:
        saveDraftError,
    },
  ] =
    useSavePublicOrgIntentionResponseMutation()

  /*
  |--------------------------------------------------------------------------
  | 7 - Submit Mutation
  |--------------------------------------------------------------------------
  */

  const [
    submitPublicOrgIntentionResponse,
    {
      isLoading:
        isSubmitting,

      isSuccess:
        isSubmitSuccess,

      isError:
        isSubmitError,

      error:
        submitError,
    },
  ] =
    useSubmitPublicOrgIntentionResponseMutation()

  /*
  |--------------------------------------------------------------------------
  | 8 - Verification Form
  |--------------------------------------------------------------------------
  */

  const [
    formData,
    setFormData,
  ] =
    useState({

      orgCode:
        "",

      passcode:
        "",

      candidateName:
        "",

      candidateEmail:
        "",

    })

  /*
  |--------------------------------------------------------------------------
  | Before You Begin Expansion - 08 Sep 2026
  |--------------------------------------------------------------------------
  */

  const [
    showFullBoardNarrative,
    setShowFullBoardNarrative,
    ] =
    useState(
        false
  )

  /*
  |--------------------------------------------------------------------------
  | 9 - Verified Access
  |--------------------------------------------------------------------------
  */

  const [
    verifiedAccess,
    setVerifiedAccess,
  ] =
    useState(null)

  /*
  |--------------------------------------------------------------------------
  | 10 - Answers
  |--------------------------------------------------------------------------
  */

  const [
    answers,
    setAnswers,
  ] =
    useState({})

  /*
  |--------------------------------------------------------------------------
  | 11 - Unsaved Changes
  |--------------------------------------------------------------------------
  */

  const [
    hasUnsavedChanges,
    setHasUnsavedChanges,
  ] =
    useState(false)

  /*
  |--------------------------------------------------------------------------
  | 12 - Submit Confirmation
  |--------------------------------------------------------------------------
  */

  const [
    isSubmitModalOpen,
    setIsSubmitModalOpen,
  ] =
    useState(false)

  /*
  |--------------------------------------------------------------------------
  | 13 - Public Data
  |--------------------------------------------------------------------------
  */

  const board =
    publicIntentionData
      ?.board ||
    {}

  const candidate =
    publicIntentionData
      ?.candidate ||
    {}

  const questions =
    publicIntentionData
      ?.questions ||
    []

  const response =
    publicIntentionData
      ?.response ||
    null

  const responseWindowOpen =
    publicIntentionData
      ?.responseWindowOpen ??
    false

  const canEdit =
    publicIntentionData
      ?.canEdit ??
    false

  /*
  |--------------------------------------------------------------------------
  | 14 - Response State
  |--------------------------------------------------------------------------
  */

  const isSubmitted =
    response
      ?.responseStatus ===
    "submitted"

  const responseComplete =
    response
      ?.isComplete ===
      true ||
    response
      ?.completionPercentage ===
      100

  const boardTimezone =
    board
      ?.timezone ||
    "America/Los_Angeles"

  /*
  |--------------------------------------------------------------------------
  | 15 - Timeline Dates
  |--------------------------------------------------------------------------
  */

  const openedDate =
    board
      ?.responseOpenedAt
      ? new Date(
          board.responseOpenedAt
        )
      : null

  const deadlineDate =
    board
      ?.responseDeadline
      ? new Date(
          board.responseDeadline
        )
      : null

  const deadlinePassed =
    deadlineDate
      ? (
          deadlineDate.getTime() <=
          currentDateTime.getTime()
        )
      : false

  /*
  |--------------------------------------------------------------------------
  | 16 - Overdue State
  |--------------------------------------------------------------------------
  */

  const isOverdueIncomplete =
    deadlinePassed &&
    !isSubmitted &&
    !responseComplete

  /*
  |--------------------------------------------------------------------------
  | 17 - Submission State
  |--------------------------------------------------------------------------
  */

  const submittedAt =
    response
      ?.submittedAt
      ? new Date(
          response.submittedAt
        )
      : null

  const isAutomaticSubmission =
    response
      ?.submissionMode ===
    "automatic_deadline"

  /*
  |--------------------------------------------------------------------------
  | 18 - Read Only
  |--------------------------------------------------------------------------
  */

  const isReadOnly =
    isSubmitted ||
    deadlinePassed ||
    !canEdit

  /*
  |--------------------------------------------------------------------------
  | 19 - Response Window Timeline
  |--------------------------------------------------------------------------
  */

  const hasResponseWindowTimeline =
    Boolean(
      openedDate &&
      deadlineDate &&
      deadlineDate.getTime() >
        openedDate.getTime()
    )

  const responseWindowDuration =
    hasResponseWindowTimeline
      ? (
          deadlineDate.getTime() -
          openedDate.getTime()
        )
      : 0

  const elapsedResponseWindow =
    hasResponseWindowTimeline
      ? (
          currentDateTime.getTime() -
          openedDate.getTime()
        )
      : 0

  const rawTimelinePercentage =
    responseWindowDuration >
    0
      ? (
          elapsedResponseWindow /
          responseWindowDuration
        ) *
        100
      : 0

  const timelinePercentage =
    Math.min(
      100,
      Math.max(
        0,
        rawTimelinePercentage
      )
    )

  /*
  |--------------------------------------------------------------------------
  | Keep Dynamic Label Away From Extreme Edges
  |--------------------------------------------------------------------------
  */

  const timelineLabelPercentage =
    Math.min(
      92,
      Math.max(
        8,
        timelinePercentage
      )
    )

  /*
  |--------------------------------------------------------------------------
  | 20 - Verification Form Change
  |--------------------------------------------------------------------------
  */

  const handleChange =
    e => {

      const {
        name,
        value,
      } =
        e.target

      setFormData(
        prev => ({

          ...prev,

          [name]:
            value,

        })
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 21 - Verify Candidate
  |--------------------------------------------------------------------------
  */

  const handleSubmit =
    async e => {

      e.preventDefault()

      const verificationData = {

        publicSlug,

        orgCode:
          formData
            .orgCode
            .trim(),

        passcode:
          formData
            .passcode,

        candidateName:
          formData
            .candidateName
            .trim(),

        candidateEmail:
          formData
            .candidateEmail
            .trim()
            .toLowerCase(),

      }

      try {

        const result =
          await verifyOrgIntentionPublicAccess(
            verificationData
          )
            .unwrap()

        setVerifiedAccess(
          result
        )

        setCurrentDateTime(
          new Date()
        )

      } catch (
        error
      ) {

        console.error(
          error
        )

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 22 - Load Public Response
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    const intentionAccessToken =
      verifiedAccess
        ?.intentionAccessToken

    if (
      !intentionAccessToken
    ) {

      return

    }

    getPublicOrgIntentionResponse({

      intentionAccessToken,

    })

  }, [
    verifiedAccess,
  ])

  /*
  |--------------------------------------------------------------------------
  | 23 - Adaptive Deadline Refresh Interval
  |--------------------------------------------------------------------------
  */

  const getDeadlineRefreshInterval =
    remainingMilliseconds => {

      const oneMinute =
        60 *
        1000

      const fifteenMinutes =
        15 *
        oneMinute

      const oneHour =
        60 *
        oneMinute

      if (
        remainingMilliseconds <=
        0
      ) {

        return null

      }

      if (
        remainingMilliseconds >
        oneHour
      ) {

        return oneHour

      }

      if (
        remainingMilliseconds >
        fifteenMinutes
      ) {

        return (
          5 *
          oneMinute
        )

      }

      return oneMinute

    }

  /*
  |--------------------------------------------------------------------------
  | 24 - Adaptive Time / Backend Reconciliation Refresh
  |--------------------------------------------------------------------------
  |
  | The frontend does NOT automatically submit responses.
  |
  | At the deadline it simply re-fetches the public state.
  |
  | The backend then decides:
  |
  | Complete saved draft -> automatic_deadline submission
  | Incomplete draft     -> remains overdue / incomplete
  |
  */

  useEffect(() => {

    const responseDeadline =
      publicIntentionData
        ?.board
        ?.responseDeadline

    const intentionAccessToken =
      verifiedAccess
        ?.intentionAccessToken

    if (
      !responseDeadline ||
      !intentionAccessToken
    ) {

      return

    }

    const deadline =
      new Date(
        responseDeadline
      )

    const now =
      new Date()

    const remainingMilliseconds =
      deadline.getTime() -
      now.getTime()

    /*
    |--------------------------------------------------------------------------
    | Already Beyond Deadline
    |--------------------------------------------------------------------------
    |
    | The GET that loaded this state has already allowed the backend to run
    | deadline reconciliation.
    |
    */

    if (
      remainingMilliseconds <=
      0
    ) {

      setCurrentDateTime(
        now
      )

      return

    }

    const refreshInterval =
      getDeadlineRefreshInterval(
        remainingMilliseconds
      )

    if (
      !refreshInterval
    ) {

      return

    }

    /*
    |--------------------------------------------------------------------------
    | Make Sure We Refresh At Deadline
    |--------------------------------------------------------------------------
    */

    const nextRefresh =
      Math.min(
        refreshInterval,
        remainingMilliseconds
      )

    const timer =
      setTimeout(
        async () => {

          setCurrentDateTime(
            new Date()
          )

          try {

            await getPublicOrgIntentionResponse({

              intentionAccessToken,

            })
              .unwrap()

          } catch (
            error
          ) {

            console.error(
              error
            )

          }

        },
        nextRefresh
      )

    return () => {

      clearTimeout(
        timer
      )

    }

  }, [
    currentDateTime,
    publicIntentionData
      ?.board
      ?.responseDeadline,
    verifiedAccess
      ?.intentionAccessToken,
  ])

  /*
  |--------------------------------------------------------------------------
  | 25 - Hydrate Answers
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    if (
      hasUnsavedChanges
    ) {

      return

    }

    const existingQuestions =
      publicIntentionData
        ?.questions ||
      []

    const existingAnswers =
      publicIntentionData
        ?.response
        ?.answers ||
      []

    if (
      !existingQuestions.length
    ) {

      return

    }

    const hydratedAnswers = {}

    existingQuestions.forEach(
      question => {

        const existingAnswer =
          existingAnswers.find(
            answer => {

              const answerQuestionId =
                answer
                  ?.questionId
                  ?._id ||
                answer
                  ?.questionId

              return (
                answerQuestionId
                  ?.toString() ===
                question
                  ?._id
                  ?.toString()
              )

            }
          )

        hydratedAnswers[
          question._id
        ] = {

          responseText:
            existingAnswer
              ?.responseText ||
            "",

          selectedOptions:
            existingAnswer
              ?.selectedOptions ||
            [],

          confirmationValue:
            existingAnswer
              ?.confirmationValue ??
            false,

        }

      }
    )

    setAnswers(
      hydratedAnswers
    )

  }, [
    publicIntentionData,
    hasUnsavedChanges,
  ])

  /*
  |--------------------------------------------------------------------------
  | 26 - Verification Validation
  |--------------------------------------------------------------------------
  */

  const canVerify =
    Boolean(
      publicSlug &&
      formData.orgCode.trim() &&
      formData.passcode &&
      formData.candidateEmail.trim()
    )

  /*
  |--------------------------------------------------------------------------
  | 27 - Format Date / Time
  |--------------------------------------------------------------------------
  */

  const formatDateTime =
    (
      dateValue,
      timezone
    ) => {

      if (
        !dateValue
      ) {

        return "Not Available"

      }

      try {

        return new Intl.DateTimeFormat(
          undefined,
          {
            timeZone:
              timezone,

            year:
              "numeric",

            month:
              "short",

            day:
              "numeric",

            hour:
              "numeric",

            minute:
              "2-digit",

            hour12:
              true,

            timeZoneName:
              "short",
          }
        )
          .format(
            new Date(
              dateValue
            )
          )

      } catch (
        error
      ) {

        console.error(
          error
        )

        return new Date(
          dateValue
        )
          .toLocaleString()

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 28 - Format Duration
  |--------------------------------------------------------------------------
  */

  const formatDuration =
    durationMilliseconds => {

      if (
        durationMilliseconds <=
        0
      ) {

        return "0 min"

      }

      const totalMinutes =
        Math.floor(
          durationMilliseconds /
          (
            60 *
            1000
          )
        )

      const days =
        Math.floor(
          totalMinutes /
          (
            24 *
            60
          )
        )

      const remainingAfterDays =
        totalMinutes %
        (
          24 *
          60
        )

      const hours =
        Math.floor(
          remainingAfterDays /
          60
        )

      const minutes =
        remainingAfterDays %
        60

      const parts = []

      if (
        days >
        0
      ) {

        parts.push(
          `${days} ${
            days ===
            1
              ? "day"
              : "days"
          }`
        )

      }

      if (
        hours >
        0
      ) {

        parts.push(
          `${hours} ${
            hours ===
            1
              ? "hr"
              : "hrs"
          }`
        )

      }

      if (
        minutes >
        0
      ) {

        parts.push(
          `${minutes} min`
        )

      }

      if (
        !parts.length
      ) {

        return "Less than 1 min"

      }

      return parts.join(
        ", "
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 29 - Remaining Time
  |--------------------------------------------------------------------------
  */

  const formatRemainingTime =
    (
      currentValue,
      deadlineValue
    ) => {

      if (
        !currentValue ||
        !deadlineValue
      ) {

        return ""

      }

      const remainingMilliseconds =
        new Date(
          deadlineValue
        )
          .getTime() -
        new Date(
          currentValue
        )
          .getTime()

      if (
        remainingMilliseconds <=
        0
      ) {

        return "Deadline reached"

      }

      return formatDuration(
        remainingMilliseconds
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 30 - Submitted Early Duration
  |--------------------------------------------------------------------------
  */

  const submittedBeforeDeadlineDuration =
    isSubmitted &&
    submittedAt &&
    deadlineDate &&
    submittedAt.getTime() <
      deadlineDate.getTime()
      ? formatDuration(
          deadlineDate.getTime() -
          submittedAt.getTime()
        )
      : null

  /*
  |--------------------------------------------------------------------------
  | 31 - Remaining Time
  |--------------------------------------------------------------------------
  */

  const remainingTime =
    formatRemainingTime(
      currentDateTime,
      board
        ?.responseDeadline
    )

  /*
  |--------------------------------------------------------------------------
  | 32 - Question Type Label
  |--------------------------------------------------------------------------
  */

  const getQuestionTypeLabel =
    questionType => {

      switch (
        questionType
      ) {

        case "reflection":
          return "Reflection"

        case "short_text":
          return "Short Response"

        case "long_text":
          return "Long Response"

        case "single_select":
          return "Select One"

        case "multi_select":
          return "Select Multiple"

        case "checkbox_confirmation":
          return "Confirmation"

        default:
          return questionType

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 33 - Response Status
  |--------------------------------------------------------------------------
  */

  const getResponseStatusLabel =
    responseStatus => {

      switch (
        responseStatus
      ) {

        case "draft":
          return "Draft In Progress"

        case "submitted":
          return "Submitted"

        default:
          return "Not Started"

      }

    }

  const getResponseStatusClass =
    responseStatus => {

      switch (
        responseStatus
      ) {

        case "submitted":
          return "bg-lime-100 text-lime-700"

        case "draft":
          return "bg-yellow-100 text-yellow-700"

        default:
          return "bg-gray-100 text-gray-600"

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 34 - Get Question Answer
  |--------------------------------------------------------------------------
  */

  const getQuestionAnswer =
    questionId => {

      return (
        answers[
          questionId
        ] ||
        {

          responseText:
            "",

          selectedOptions:
            [],

          confirmationValue:
            false,

        }
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 35 - Text Answer
  |--------------------------------------------------------------------------
  */

  const handleTextResponseChange =
    (
      questionId,
      value
    ) => {

      setHasUnsavedChanges(
        true
      )

      setAnswers(
        prev => ({

          ...prev,

          [questionId]: {

            ...(
              prev[
                questionId
              ] ||
              {
                responseText:
                  "",

                selectedOptions:
                  [],

                confirmationValue:
                  false,
              }
            ),

            responseText:
              value,

          },

        })
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 36 - Single Select
  |--------------------------------------------------------------------------
  */

  const handleSingleSelectChange =
    (
      questionId,
      optionValue
    ) => {

      setHasUnsavedChanges(
        true
      )

      setAnswers(
        prev => ({

          ...prev,

          [questionId]: {

            ...(
              prev[
                questionId
              ] ||
              {}
            ),

            selectedOptions: [
              optionValue,
            ],

          },

        })
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 37 - Multi Select
  |--------------------------------------------------------------------------
  */

  const handleMultiSelectChange =
    (
      questionId,
      optionValue,
      checked
    ) => {

      setHasUnsavedChanges(
        true
      )

      setAnswers(
        prev => {

          const currentAnswer =
            prev[
              questionId
            ] ||
            {

              responseText:
                "",

              selectedOptions:
                [],

              confirmationValue:
                false,

            }

          const currentOptions =
            currentAnswer
              ?.selectedOptions ||
            []

          const updatedOptions =
            checked
              ? (
                  currentOptions.includes(
                    optionValue
                  )
                    ? currentOptions
                    : [
                        ...currentOptions,
                        optionValue,
                      ]
                )
              : currentOptions.filter(
                  item =>
                    item !==
                    optionValue
                )

          return {

            ...prev,

            [questionId]: {

              ...currentAnswer,

              selectedOptions:
                updatedOptions,

            },

          }

        }
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 38 - Confirmation
  |--------------------------------------------------------------------------
  */

  const handleConfirmationChange =
    (
      questionId,
      checked
    ) => {

      setHasUnsavedChanges(
        true
      )

      setAnswers(
        prev => ({

          ...prev,

          [questionId]: {

            ...(
              prev[
                questionId
              ] ||
              {}
            ),

            confirmationValue:
              checked,

          },

        })
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 39 - Build Answers Payload
  |--------------------------------------------------------------------------
  */

  const buildAnswersPayload =
    questionsToBuild => {

      return questionsToBuild.map(
        question => {

          const answer =
            getQuestionAnswer(
              question._id
            )

          return {

            questionId:
              question._id,

            responseText:
              answer
                ?.responseText ||
              "",

            selectedOptions:
              answer
                ?.selectedOptions ||
              [],

            confirmationValue:
              answer
                ?.confirmationValue ??
              false,

          }

        }
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 40 - Save Draft
  |--------------------------------------------------------------------------
  */

  const handleSaveDraft =
    async () => {

      const intentionAccessToken =
        verifiedAccess
          ?.intentionAccessToken

      if (
        !intentionAccessToken ||
        !questions.length
      ) {

        return

      }

      const answersPayload =
        buildAnswersPayload(
          questions
        )

      try {

        await savePublicOrgIntentionResponse({

          intentionAccessToken,

          answers:
            answersPayload,

        })
          .unwrap()

        setHasUnsavedChanges(
          false
        )

        await getPublicOrgIntentionResponse({

          intentionAccessToken,

        })
          .unwrap()

        setCurrentDateTime(
          new Date()
        )

      } catch (
        error
      ) {

        console.error(
          error
        )

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 41 - Submit Modal
  |--------------------------------------------------------------------------
  */

  const handleOpenSubmitModal =
    () => {

      setIsSubmitModalOpen(
        true
      )

    }

  const handleCloseSubmitModal =
    () => {

      setIsSubmitModalOpen(
        false
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 42a - Manual Submit
  |--------------------------------------------------------------------------
  |
  | Save the candidate's CURRENT browser answers first.
  |
  | The backend submit endpoint intentionally submits only the persisted
  | response, so this guarantees that what the candidate currently sees
  | is what gets submitted.
  |
  */

  const handleSubmitResponse =
    async e => {

      if (
        e
          ?.preventDefault
      ) {

        e.preventDefault()

      }

      const intentionAccessToken =
        verifiedAccess
          ?.intentionAccessToken

      if (
        !intentionAccessToken ||
        !questions.length
      ) {

        return

      }

      const answersPayload =
        buildAnswersPayload(
          questions
        )

      try {

        /*
        |--------------------------------------------------------------------------
        | Save Current Screen State
        |--------------------------------------------------------------------------
        */

        await savePublicOrgIntentionResponse({

          intentionAccessToken,

          answers:
            answersPayload,

        })
          .unwrap()

        /*
        |--------------------------------------------------------------------------
        | Submit Persisted Response
        |--------------------------------------------------------------------------
        */

        await submitPublicOrgIntentionResponse({

          intentionAccessToken,

        })
          .unwrap()

        setHasUnsavedChanges(
          false
        )

        setIsSubmitModalOpen(
          false
        )

        await getPublicOrgIntentionResponse({

          intentionAccessToken,

        })
          .unwrap()

        setCurrentDateTime(
          new Date()
        )

      } catch (
        error
      ) {

        console.error(
          error
        )

      }

    }

  // 42b - Create an object for setting inner HTML - 08 Sep 2026

  const createMarkup =
    htmlContent => {

        return {
        __html:
            htmlContent,
        }

    }

  /*
  |--------------------------------------------------------------------------
  | 43 - Read Only Reason
  |--------------------------------------------------------------------------
  */

  const getReadOnlyReason =
    () => {

      if (
        isSubmitted
      ) {

        return "Your response has already been submitted and is now read only."

      }

      if (
        isOverdueIncomplete
      ) {

        return "The response deadline has passed. Your saved response was incomplete and cannot now be submitted."

      }

      if (
        board
          ?.isActive ===
        false
      ) {

        return "This Intention Board is currently inactive."

      }

      if (
        board
          ?.boardStatus &&
        board.boardStatus !==
        "open"
      ) {

        return (
          `This Intention Board is currently ${board.boardStatus}. ` +
          `Responses can be entered when the board is open.`
        )

      }

      if (
        !responseWindowOpen
      ) {

        return "The response window for this Intention Board is currently closed."

      }

      return "This Intention Board is currently read only."

    }

  /*
  |--------------------------------------------------------------------------
  | 44 - Verification Screen
  |--------------------------------------------------------------------------
  */

  if (
    !verifiedAccess
      ?.intentionAccessToken
  ) {

    return (

      <div className="min-h-screen bg-gray-100 font-poppins p-4">

        <div className="max-w-xl mx-auto mt-10">

          <div className="bg-white rounded-lg shadow-md p-6">

            <div className="mb-6">

              <p className="text-sm font-bold text-orange-600">
                Trinity
              </p>

              <p className="text-2xl font-bold text-gray-800 mt-1">
                Intention Board
              </p>

              <p className="text-sm text-gray-600 mt-3">

                This space is intended for leaders who have been nominated
                to participate in this Intention Board.

              </p>

              <p className="text-xs text-gray-500 mt-2">

                Please enter your organization access details and the
                information used for your nomination.

              </p>

            </div>

            {isVerifying && (

              <div className="flex justify-center items-center py-3">

                <PulseLoader
                  color={"#ea580c"}
                  size={7}
                />

              </div>

            )}

            {isVerificationError && (

              <div className="bg-orange-50 border border-orange-200 rounded p-3 mb-4">

                <p className="text-xs font-bold text-orange-700">
                  We could not verify your access.
                </p>

                <p className="text-xs text-orange-700 mt-1">

                  {
                    verificationError
                      ?.data
                      ?.message ||
                    "Please check the information entered and try again."
                  }

                </p>

              </div>

            )}

            <form
              onSubmit={
                handleSubmit
              }
            >

              <div className="mb-3">

                <label
                  htmlFor="orgCode"
                  className="block text-xs font-bold text-gray-700 mb-1"
                >
                  Organization Code
                </label>

                <input
                  type="text"
                  id="orgCode"
                  name="orgCode"
                  value={
                    formData.orgCode
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Enter your organization code"
                  autoComplete="off"
                  className="w-full rounded border border-gray-300 p-3 text-sm focus:outline-none focus:border-orange-500"
                />

              </div>

              <div className="mb-3">

                <label
                  htmlFor="passcode"
                  className="block text-xs font-bold text-gray-700 mb-1"
                >
                  Access Passcode
                </label>

                <input
                  type="password"
                  id="passcode"
                  name="passcode"
                  value={
                    formData.passcode
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Enter your access passcode"
                  autoComplete="off"
                  className="w-full rounded border border-gray-300 p-3 text-sm focus:outline-none focus:border-orange-500"
                />

              </div>

              <div className="mb-3">

                <label
                  htmlFor="candidateName"
                  className="block text-xs font-bold text-gray-700 mb-1"
                >
                  Your Name
                </label>

                <input
                  type="text"
                  id="candidateName"
                  name="candidateName"
                  value={
                    formData.candidateName
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Enter your name"
                  autoComplete="name"
                  className="w-full rounded border border-gray-300 p-3 text-sm focus:outline-none focus:border-orange-500"
                />

              </div>

              <div className="mb-5">

                <label
                  htmlFor="candidateEmail"
                  className="block text-xs font-bold text-gray-700 mb-1"
                >
                  Your Email
                </label>

                <input
                  type="email"
                  id="candidateEmail"
                  name="candidateEmail"
                  value={
                    formData.candidateEmail
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Enter the email used for your nomination"
                  autoComplete="email"
                  className="w-full rounded border border-gray-300 p-3 text-sm focus:outline-none focus:border-orange-500"
                />

                <p className="text-[10px] text-gray-500 mt-1">

                  Please use the same email address with which you were
                  nominated for this Intention Board.

                </p>

              </div>

              <button
                type="submit"
                disabled={
                  !canVerify ||
                  isVerifying
                }
                className={`w-full rounded px-4 py-3 text-sm font-bold ${
                  canVerify &&
                  !isVerifying
                    ? "bg-orange-600 hover:bg-orange-700 text-white cursor-pointer"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >

                Continue

              </button>

            </form>

          </div>

        </div>

      </div>

    )

  }

  /*
  |--------------------------------------------------------------------------
  | 45 - Initial Loading
  |--------------------------------------------------------------------------
  */

  if (
    isFetchingPublicIntention &&
    !publicIntentionData
  ) {

    return (

      <div className="min-h-screen bg-gray-100 font-poppins flex justify-center items-center">

        <PulseLoader
          color={"#ea580c"}
        />

      </div>

    )

  }

  /*
  |--------------------------------------------------------------------------
  | 46 - Public Load Error
  |--------------------------------------------------------------------------
  */

  if (
    isPublicIntentionError &&
    !publicIntentionData
  ) {

    return (

      <div className="min-h-screen bg-gray-100 font-poppins p-4">

        <div className="max-w-xl mx-auto mt-10">

          <div className="bg-white rounded-lg shadow-md p-6">

            <p className="text-sm font-bold text-orange-700">
              Unable to load your Intention Board
            </p>

            <p className="text-xs text-gray-600 mt-2">

              {
                publicIntentionError
                  ?.data
                  ?.message ||
                "Please verify your access again."
              }

            </p>

          </div>

        </div>

      </div>

    )

  }

  /*
  |--------------------------------------------------------------------------
  | 47 - Render
  |--------------------------------------------------------------------------
  */

  return (

    <div className="min-h-screen bg-gray-100 font-poppins p-4">

      <div className="max-w-4xl mx-auto mt-6 mb-10">

        {/* Header */}

        <div className="bg-white rounded-lg shadow-md p-6">

          <p className="text-sm font-bold text-orange-600">
            Trinity
          </p>

          <p className="text-2xl font-bold text-gray-800 mt-1">

            {
              board
                ?.headline ||
              "Intention Board"
            }

          </p>

          {candidate
            ?.candidateName && (

            <p className="text-sm text-gray-600 mt-3">

              Welcome,{" "}

              <span className="font-bold">

                {
                  candidate
                    .candidateName
                }

              </span>

            </p>

          )}

          {/* Status */}

          <div className="flex justify-start items-center flex-wrap mt-4">

            <p
              className={`text-[10px] font-bold rounded-full px-3 py-1 mr-2 ${
                getResponseStatusClass(
                  response
                    ?.responseStatus
                )
              }`}
            >

              {
                getResponseStatusLabel(
                  response
                    ?.responseStatus
                )
              }

            </p>

            <p
              className={`text-[10px] font-bold rounded-full px-3 py-1 mr-2 ${
                board
                  ?.boardStatus ===
                  "open"
                  ? "bg-lime-100 text-lime-700"
                  : "bg-yellow-100 text-yellow-700"
              }`}
            >

              Board: {
                board
                  ?.boardStatus ||
                "Unknown"
              }

            </p>

            <p
              className={`text-[10px] font-bold rounded-full px-3 py-1 ${
                responseWindowOpen
                  ? "bg-lime-100 text-lime-700"
                  : "bg-gray-200 text-gray-600"
              }`}
            >

              {
                responseWindowOpen
                  ? "Response Window Open"
                  : "Response Window Closed"
              }

            </p>

          </div>

          {/* Response Deadline */}

          {board
            ?.responseDeadline && (

            <div className="mt-4 bg-gray-50 rounded p-4">

              <p className="text-xs font-bold text-gray-800">
                Response Deadline
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">

                {/* Board Time */}

                <div className="bg-white rounded p-3">

                  <p className="text-[9px] font-bold text-gray-500 uppercase">
                    Board Time
                  </p>

                  <p className="text-xs font-bold text-gray-700 mt-1">

                    {
                      formatDateTime(
                        board
                          .responseDeadline,
                        boardTimezone
                      )
                    }

                  </p>

                  <p className="text-[9px] text-gray-500 mt-1">
                    {boardTimezone}
                  </p>

                </div>

                {/* User Time */}

                <div className="bg-white rounded p-3">

                  <p className="text-[9px] font-bold text-orange-600 uppercase">
                    Your Time
                  </p>

                  <p className="text-xs font-bold text-gray-700 mt-1">

                    {
                      formatDateTime(
                        board
                          .responseDeadline,
                        userTimezone
                      )
                    }

                  </p>

                  <p className="text-[9px] text-gray-500 mt-1">
                    {userTimezone}
                  </p>

                </div>

                {/* Current Time */}

                <div className="bg-white rounded p-3">

                  <p className="text-[9px] font-bold text-gray-500 uppercase">
                    Current Time Where You Are
                  </p>

                  <p className="text-xs font-bold text-gray-700 mt-1">

                    {
                      formatDateTime(
                        currentDateTime,
                        userTimezone
                      )
                    }

                  </p>

                  <p className="text-[9px] text-gray-500 mt-1">
                    {userTimezone}
                  </p>

                </div>

              </div>

              {/* Submitted */}

              {isSubmitted && (

                <div className="bg-white rounded p-4 mt-3">

                  {isAutomaticSubmission ? (

                    <>

                      <p className="text-[10px] font-bold text-lime-700 uppercase">
                        Automatically Submitted
                      </p>

                      <p className="text-sm font-bold text-gray-800 mt-2">

                        Your last fully saved response was submitted
                        automatically on your behalf at the deadline.

                      </p>

                      {submittedAt && (

                        <p className="text-[10px] text-gray-500 mt-2">

                          Submitted:{" "}

                          {
                            formatDateTime(
                              submittedAt,
                              userTimezone
                            )
                          }

                        </p>

                      )}

                    </>

                  ) : (

                    <>

                      <p className="text-[10px] font-bold text-lime-700 uppercase">
                        Submitted
                      </p>

                      <p className="text-sm font-bold text-gray-800 mt-2">

                        You submitted{" "}

                        <span className="text-orange-700">

                          {
                            submittedBeforeDeadlineDuration ||
                            "less than 1 min"
                          }

                        </span>

                        {" "}before the deadline.

                      </p>

                      {submittedAt && (

                        <p className="text-[10px] text-gray-500 mt-2">

                          Submitted:{" "}

                          {
                            formatDateTime(
                              submittedAt,
                              userTimezone
                            )
                          }

                        </p>

                      )}

                    </>

                  )}

                </div>

              )}

              {/* Overdue Incomplete */}

              {isOverdueIncomplete && (

                <div className="bg-orange-50 border border-orange-200 rounded p-4 mt-3">

                  <p className="text-[10px] font-bold text-orange-700 uppercase">
                    Response Overdue
                  </p>

                  <p className="text-sm font-bold text-gray-800 mt-2">
                    Your response was not submitted before the deadline.
                  </p>

                  <p className="text-xs text-gray-600 mt-2">

                    The last saved response was incomplete and can no longer
                    be submitted.

                  </p>

                  <p className="text-[10px] text-gray-500 mt-2">

                    Saved completion:{" "}

                    {
                      response
                        ?.completionPercentage ??
                      0
                    }
                    %

                  </p>

                </div>

              )}

              {/* Active Response Window Timeline */}

              {!isSubmitted &&
                !deadlinePassed && (

                <div className="bg-white rounded p-4 mt-3">

                  {hasResponseWindowTimeline ? (

                    <>

                      {/* Labels */}

                      <div className="relative h-5">

                        <p className="absolute left-0 text-[9px] font-bold text-gray-500 uppercase">
                          Opened
                        </p>

                        <p
                          className="absolute text-[9px] font-bold text-orange-700 uppercase -translate-x-1/2"
                          style={{
                            left:
                              `${timelineLabelPercentage}%`,
                          }}
                        >
                          Now
                        </p>

                        <p className="absolute right-0 text-[9px] font-bold text-gray-500 uppercase">
                          Deadline
                        </p>

                      </div>

                      {/* Timeline */}

                      <div className="relative h-5 mt-1">

                        {/* Full Window */}

                        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] bg-gray-300" />

                        {/* Elapsed Window */}

                        <div
                          className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] bg-orange-500"
                          style={{
                            width:
                              `${timelinePercentage}%`,
                          }}
                        />

                        {/* Opened */}

                        <div className="absolute left-0 top-1/2 -translate-y-1/2 h-3 w-3 rounded-full bg-orange-600" />

                        {/* Now */}

                        <div
                          className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-orange-600 border-2 border-white shadow"
                          style={{
                            left:
                              `${timelinePercentage}%`,
                          }}
                        />

                        {/* Deadline */}

                        <div className="absolute right-0 top-1/2 -translate-y-1/2 h-3 w-3 rounded-full bg-orange-600" />

                      </div>

                      {/* Times */}

                      <div className="grid grid-cols-3 gap-3 mt-2">

                        <div>

                          <p className="text-[9px] text-gray-500">

                            {
                              formatDateTime(
                                openedDate,
                                userTimezone
                              )
                            }

                          </p>

                        </div>

                        <div className="text-center">

                          <p className="text-[10px] font-bold text-orange-700">

                            {
                              Math.round(
                                timelinePercentage
                              )
                            }
                            % elapsed

                          </p>

                        </div>

                        <div className="text-right">

                          <p className="text-xs font-bold text-orange-700">
                            {remainingTime}
                          </p>

                          <p className="text-[9px] text-gray-500 mt-1">
                            remaining
                          </p>

                        </div>

                      </div>

                    </>

                  ) : (

                    /*
                    |--------------------------------------------------------------------------
                    | Legacy Board Fallback
                    |--------------------------------------------------------------------------
                    |
                    | Boards opened before responseOpenedAt was introduced do
                    | not have enough information to calculate elapsed position.
                    |
                    */

                    <div>

                      <p className="text-[9px] font-bold text-gray-500 uppercase">
                        Time Remaining
                      </p>

                      <p className="text-sm font-bold text-orange-700 mt-1">
                        {remainingTime}
                      </p>

                    </div>

                  )}

                </div>

              )}

            </div>

          )}

        </div>

        {/* Read Only Warning */}

        {isReadOnly &&
          !isSubmitted &&
          !isOverdueIncomplete && (

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">

            <p className="text-xs font-bold text-yellow-800">
              Responses are currently read only.
            </p>

            <p className="text-xs text-yellow-700 mt-1">
              {getReadOnlyReason()}
            </p>

          </div>

        )}

        {/* Narrative  - Updated 08 Sep 2026 - Rich Text with show / hide*/}

        {board
            ?.boardNarrative && (

        <div className="bg-white rounded-lg shadow-md p-6 mt-4">

            {/*
            |--------------------------------------------------------------------------
            | Header
            |--------------------------------------------------------------------------
            */}

            <div className="flex justify-start items-center mb-3">

                <div
                    onClick={
                    () =>
                        setShowFullBoardNarrative(
                        prev =>
                            !prev
                        )
                    }
                    className="
                    h-7
                    w-7
                    rounded-full
                    flex
                    justify-center
                    items-center
                    cursor-pointer
                    hover:bg-gray-100
                    "
                    title={
                    showFullBoardNarrative
                        ? "Show less"
                        : "Show more"
                    }
                >

                    {showFullBoardNarrative ? (

                    <MinusIcon
                        className="h-5 w-5 text-orange-600"
                    />

                    ) : (

                    <PlusIcon
                        className="h-5 w-5 text-orange-600"
                    />

                    )}

                </div>

            <p className="text-sm font-bold text-gray-800 mx-2">
                Before You Begin
            </p>

            

            </div>

            {/*
            |--------------------------------------------------------------------------
            | Narrative
            |--------------------------------------------------------------------------
            */}

            <div>

            <div
                className={`
                text-sm
                text-gray-700
                leading-relaxed
                [&_p]:mb-2
                [&_ul]:list-disc
                [&_ul]:ml-5
                [&_ul]:mb-2
                [&_ol]:list-decimal
                [&_ol]:ml-5
                [&_ol]:mb-2
                [&_li]:mb-1
                [&_strong]:font-bold
                [&_a]:text-orange-600
                [&_a]:underline
                ${
                    showFullBoardNarrative
                    ? ""
                    : "line-clamp-3"
                }
                `}
                dangerouslySetInnerHTML={
                createMarkup(
                    board
                    .boardNarrative
                )
                }
            />

            {/*
            |--------------------------------------------------------------------------
            | Read More / Less
            |--------------------------------------------------------------------------
            */}

            <div className="flex justify-end items-center mt-2">

                <button
                type="button"
                onClick={
                    () =>
                    setShowFullBoardNarrative(
                        prev =>
                        !prev
                    )
                }
                className="
                    text-xs
                    font-bold
                    text-orange-600
                    hover:text-orange-700
                    hover:underline
                    cursor-pointer
                "
                >

                {
                    showFullBoardNarrative
                    ? "Read Less"
                    : "Read More"
                }

                </button>

            </div>

            </div>

        </div>

        )}

        {/* {board
          ?.boardNarrative && (

          <div className="bg-white rounded-lg shadow-md p-6 mt-4">
            

            <p className="text-sm font-bold text-gray-800 mb-3">
              Before You Begin
            </p>

            <div
                className="text-sm text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={
                    createMarkup(
                    board
                        .boardNarrative
                    )
                }
            />

           </div>

        )} */}

        {/* Progress */}

        {!isSubmitted && (

          <div className="bg-white rounded-lg shadow-md p-4 mt-4">

            <div className="flex justify-between items-center">

              <div>

                <p className="text-xs font-bold text-gray-700">
                  Your Response
                </p>

                <p className="text-[10px] text-gray-500 mt-1">

                  Completion:{" "}

                  {
                    response
                      ?.completionPercentage ??
                    0
                  }
                  %

                </p>

              </div>

              <p
                className={`text-[10px] font-bold rounded-full px-3 py-1 ${
                  getResponseStatusClass(
                    response
                      ?.responseStatus
                  )
                }`}
              >

                {
                  getResponseStatusLabel(
                    response
                      ?.responseStatus
                  )
                }

              </p>

            </div>

          </div>

        )}

        {/* Save Success */}

        {isSaveDraftSuccess &&
          !isSubmitted && (

          <div className="bg-lime-50 border border-lime-200 rounded p-3 mt-4">

            <p className="text-xs font-bold text-lime-700">
              Draft saved successfully.
            </p>

          </div>

        )}

        {/* Save Error */}

        {isSaveDraftError && (

          <div className="bg-orange-50 border border-orange-200 rounded p-3 mt-4">

            <p className="text-xs font-bold text-orange-700">
              Unable to save your draft.
            </p>

            <p className="text-xs text-orange-700 mt-1">

              {
                saveDraftError
                  ?.data
                  ?.message ||
                "Please try again."
              }

            </p>

          </div>

        )}

        {/* Submit Success */}

        {isSubmitSuccess &&
          isSubmitted && (

          <div className="bg-lime-50 border border-lime-200 rounded p-4 mt-4">

            <p className="text-sm font-bold text-lime-700">
              Your response has been submitted successfully.
            </p>

            <p className="text-[10px] text-lime-700 mt-1">
              Your Intention Board response is now final and read only.
            </p>

          </div>

        )}

        {/* Submit Error */}

        {isSubmitError && (

          <div className="bg-orange-50 border border-orange-200 rounded p-3 mt-4">

            <p className="text-xs font-bold text-orange-700">
              Unable to submit your response.
            </p>

            <p className="text-xs text-orange-700 mt-1">

              {
                submitError
                  ?.data
                  ?.message ||
                "Please review your responses and try again."
              }

            </p>

          </div>

        )}

        {/* Questions */}

        <div className="bg-white rounded-lg shadow-md p-6 mt-4">

          <div className="mb-5">

            <p className="text-lg font-bold text-gray-800">
              Your Reflection
            </p>

            <p className="text-xs text-gray-500 mt-1">

              {
                questions.length
              }{" "}

              question{
                questions.length ===
                1
                  ? ""
                  : "s"
              }

            </p>

          </div>

          {questions
            ?.length ===
            0 && (

            <div className="bg-gray-50 rounded p-4">

              <p className="text-xs text-gray-500">
                No questions are currently available.
              </p>

            </div>

          )}

          {questions
            ?.map(
              (
                question,
                index
              ) => {

                const answer =
                  getQuestionAnswer(
                    question._id
                  )

                return (

                  <div
                    key={
                      question._id
                    }
                    className="border-b border-gray-200 py-5 last:border-b-0"
                  >

                    {/* Question */}

                    <div className="flex justify-start items-center flex-wrap">

                      <p className="text-sm font-bold text-gray-800">

                        {
                          question
                            ?.questionNumber ||
                          index +
                            1
                        }
                        .{" "}
                        {
                          question
                            ?.questionText
                        }

                      </p>

                      {question
                        ?.isRequired && (

                        <p className="text-[9px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1">
                          Required
                        </p>

                      )}

                    </div>

                    {question
                      ?.questionDescription && (

                      <p className="text-xs text-gray-600 mt-2">

                        {
                          question
                            .questionDescription
                        }

                      </p>

                    )}

                    {question
                      ?.responseGuidance && (

                      <p className="text-[10px] italic text-gray-500 mt-2">

                        {
                          question
                            .responseGuidance
                        }

                      </p>

                    )}

                    <p className="text-[9px] text-gray-400 mt-2">

                      {
                        getQuestionTypeLabel(
                          question
                            ?.questionType
                        )
                      }

                    </p>

                    {/* Text */}

                    {(
                      question
                        ?.questionType ===
                        "reflection" ||
                      question
                        ?.questionType ===
                        "short_text" ||
                      question
                        ?.questionType ===
                        "long_text"
                    ) && (

                      <textarea
                        value={
                          answer
                            ?.responseText ||
                          ""
                        }
                        onChange={
                          e =>
                            handleTextResponseChange(
                              question._id,
                              e.target.value
                            )
                        }
                        disabled={
                          isReadOnly
                        }
                        placeholder="Enter your response"
                        rows={
                          question
                            ?.questionType ===
                            "short_text"
                            ? 2
                            : 5
                        }
                        className={`w-full rounded border p-3 text-sm mt-3 ${
                          isReadOnly
                            ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                            : "bg-white border-gray-300 text-gray-700 focus:outline-none focus:border-orange-500"
                        }`}
                      />

                    )}

                    {/* Single Select */}

                    {question
                      ?.questionType ===
                      "single_select" && (

                      <div className="space-y-2 mt-3">

                        {question
                          ?.options
                          ?.map(
                            (
                              option,
                              optionIndex
                            ) => {

                              const optionValue =
                                option
                                  ?.optionValue ||
                                option
                                  ?.optionLabel

                              return (

                                <label
                                  key={
                                    optionIndex
                                  }
                                  className="flex items-center text-xs text-gray-700"
                                >

                                  <input
                                    type="radio"
                                    name={
                                      `question-${question._id}`
                                    }
                                    checked={
                                      answer
                                        ?.selectedOptions
                                        ?.includes(
                                          optionValue
                                        ) ||
                                      false
                                    }
                                    onChange={
                                      () =>
                                        handleSingleSelectChange(
                                          question._id,
                                          optionValue
                                        )
                                    }
                                    disabled={
                                      isReadOnly
                                    }
                                    className="mr-2"
                                  />

                                  {
                                    option
                                      ?.optionLabel
                                  }

                                </label>

                              )

                            }
                          )}

                      </div>

                    )}

                    {/* Multi Select */}

                    {question
                      ?.questionType ===
                      "multi_select" && (

                      <div className="space-y-2 mt-3">

                        {question
                          ?.options
                          ?.map(
                            (
                              option,
                              optionIndex
                            ) => {

                              const optionValue =
                                option
                                  ?.optionValue ||
                                option
                                  ?.optionLabel

                              return (

                                <label
                                  key={
                                    optionIndex
                                  }
                                  className="flex items-center text-xs text-gray-700"
                                >

                                  <input
                                    type="checkbox"
                                    checked={
                                      answer
                                        ?.selectedOptions
                                        ?.includes(
                                          optionValue
                                        ) ||
                                      false
                                    }
                                    onChange={
                                      e =>
                                        handleMultiSelectChange(
                                          question._id,
                                          optionValue,
                                          e.target.checked
                                        )
                                    }
                                    disabled={
                                      isReadOnly
                                    }
                                    className="mr-2"
                                  />

                                  {
                                    option
                                      ?.optionLabel
                                  }

                                </label>

                              )

                            }
                          )}

                      </div>

                    )}

                    {/* Confirmation */}

                    {question
                      ?.questionType ===
                      "checkbox_confirmation" && (

                      <label className="flex items-center text-xs text-gray-700 mt-3">

                        <input
                          type="checkbox"
                          checked={
                            answer
                              ?.confirmationValue ||
                            false
                          }
                          onChange={
                            e =>
                              handleConfirmationChange(
                                question._id,
                                e.target.checked
                              )
                          }
                          disabled={
                            isReadOnly
                          }
                          className="mr-2"
                        />

                        Confirm

                      </label>

                    )}

                  </div>

                )

              }
            )}

          {/* Response Actions */}

          {!isSubmitted &&
            !deadlinePassed &&
            questions.length >
            0 && (

            <div className="border-t border-gray-200 pt-5 mt-3">

              {(isSavingDraft ||
                isSubmitting) && (

                <div className="mb-3">

                  <PulseLoader
                    color={"#ea580c"}
                    size={6}
                  />

                </div>

              )}

              <div className="flex justify-start items-center flex-wrap gap-2">

                <button
                  type="button"
                  onClick={
                    handleSaveDraft
                  }
                  disabled={
                    isReadOnly ||
                    isSavingDraft ||
                    isSubmitting
                  }
                  className={`rounded px-5 py-2 text-sm font-bold ${
                    !isReadOnly &&
                    !isSavingDraft &&
                    !isSubmitting
                      ? "bg-orange-600 hover:bg-orange-700 text-white cursor-pointer"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >

                  Save Draft

                </button>

                <button
                  type="button"
                  onClick={
                    handleOpenSubmitModal
                  }
                  disabled={
                    isReadOnly ||
                    isSavingDraft ||
                    isSubmitting
                  }
                  className={`rounded px-5 py-2 text-sm font-bold ${
                    !isReadOnly &&
                    !isSavingDraft &&
                    !isSubmitting
                      ? "bg-lime-600 hover:bg-lime-700 text-white cursor-pointer"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >

                  Submit Response

                </button>

              </div>

              <div className="mt-2">

                <p className="text-[10px] text-gray-500">
                  Save Draft allows you to return and continue your response until the deadline.
                </p>

                <p className="text-[10px] text-gray-500 mt-1">
                  Submit Response is final and your responses cannot be edited afterward.
                </p>

                {hasUnsavedChanges && (

                  <p className="text-[10px] font-bold text-orange-600 mt-1">
                    You have unsaved changes.
                  </p>

                )}

              </div>

            </div>

          )}

          {/* Submitted Note */}

          {isSubmitted && (

            <p className="text-[10px] text-gray-500 mt-4">
              Your response is final and read only.
            </p>

          )}

          {/* Overdue Note */}

          {isOverdueIncomplete && (

            <p className="text-[10px] text-orange-700 mt-4">
              The submission deadline has passed and this response can no longer be changed or submitted.
            </p>

          )}

        </div>

        {/* Closing Narrative */}

        {board
          ?.closingNarrative && (

          <div className="bg-orange-50 border border-orange-100 rounded-lg p-5 mt-4">

            <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">

              {
                board
                  .closingNarrative
              }

            </div>

          </div>

        )}

        {/* Submit Confirmation */}

        <ModalSubmissionConfirm
          isOpen={
            isSubmitModalOpen
          }
          onClose={
            handleCloseSubmitModal
          }
          onConfirm={
            handleSubmitResponse
          }
          headerMsg={
            "Ready to submit your response?"
          }
          titleMsg={
            "Once submitted, your Intention Board response will be final and cannot be edited. Are you sure you want to continue?"
          }
        />

      </div>

    </div>

  )

}

export default EtPublicOrgIntentionBoard